import { ButtonDropdown, Text } from '@payaca/ambrosia';
import UntitledIcon from '@payaca/untitled-icons';
import { FC, useId } from 'react';

interface Contact {
  publicId: string;
  name?: string | null;
  isPrimary?: boolean | null;
  email?: string | null;
}

type RecipientPickerProps<T extends Contact> = {
  recipient: T | null;
  contacts?: T[];
  /**
   * When omitted, the recipient is rendered as pinned text rather than a
   * dropdown — e.g. once an estimate is SENT and the contact is locked in.
   */
  onSelectRecipient?: (contact: T) => void;
};

const RecipientPicker = <T extends Contact>(props: RecipientPickerProps<T>) => {
  const { recipient, contacts = [], onSelectRecipient } = props;
  const pinnedLabelId = useId();

  return (
    <>
      {onSelectRecipient ? (
        <>
          <Text as="span">
            <label htmlFor="send-to-customer-contact" className="font-medium">
              Send to
            </label>
          </Text>
          <ButtonDropdown
            id="send-to-customer-contact"
            variant="secondary"
            size="sm"
            dropdownOptions={{
              className: 'w-72',
              items: contacts.map((contact) => ({
                label: <ContactMenuItemLabel contact={contact} />,
                onClick: () => onSelectRecipient(contact),
                selected: contact.publicId === recipient?.publicId,
              })),
            }}
          >
            {recipient?.name ?? 'No contact'}
            <UntitledIcon name="chevron-down" className="size-3" />
          </ButtonDropdown>
        </>
      ) : (
        <>
          <Text as="span" id={pinnedLabelId}>
            Send to
          </Text>
          <Text as="span" aria-labelledby={pinnedLabelId}>
            <span className="font-medium mr-2">
              {recipient?.name ?? 'No contact'}
            </span>
          </Text>
        </>
      )}
    </>
  );
};

type ContactMenuItemLabelProps = {
  contact: Contact;
};

const ContactMenuItemLabel: FC<ContactMenuItemLabelProps> = (props) => {
  const { contact } = props;
  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <span className="flex items-center gap-x-2">
        <span className="truncate font-medium">
          {contact.name ?? 'Unnamed contact'}
        </span>
        {contact.isPrimary && <span className="text-gray-500">Primary</span>}
      </span>
      {contact.email && (
        <span className="truncate text-gray-500">{contact.email}</span>
      )}
    </div>
  );
};

export default RecipientPicker;
